import React, { useEffect, useState } from "react";
import SwitchButton from "../Buttons/SwitchButton";
import "./ImagerFeatureControls.css";
import ImagerFeatureControlSlider from "./ImagerFeatureControlSlider";
import CommonDropDown from "../Common/DropDown/CommonDropDown";

type ToggleControlProps = {
  id: string;
  feature: string;
  value: boolean;
  onValueChanged: (id: string, value: boolean) => void;
  disabled: boolean;
};
function ToggleControl({
  id,
  feature,
  value,
  onValueChanged,
  disabled,
}: ToggleControlProps) {
  return (
    <BaseControl key={id} id={id} disabled={disabled}>
      <FeatureLabel disabled={disabled}>{feature + ":"}</FeatureLabel>
      <div
        style={{
          marginLeft: 10,
          marginRight: 15,
        }}
      >
        {/* {value ? (
          <IconOn style={{width: 64, height: 32, margin: "auto"}}
            onClick={() => {
              onValueChanged(id, !value);
            }}
          />
        ) : (
          <IconOff style={{width: 64, height: 32, margin: "auto"}}
            onClick={() => {
              onValueChanged(id, !value);
            }}
          />
        )} */}
        <SwitchButton
          isOn={value}
          onToggle={() => onValueChanged(id, !value)}
          withLetter={true}
        />
      </div>
    </BaseControl>
  );
}

type BasicControlProps = {
  max: number;
  min: number;
  value: number;
  onValueChanged: (id: string, value: number) => void;
  id: string;
  feature: string;
  unit?: string;
  step: number;
  disabled: boolean;
};

function BasicControl({
  max,
  min,
  value,
  onValueChanged,
  id,
  feature,
  unit,
  step,
  disabled,
}: BasicControlProps) {
  const [controlValue, setControlValue] = useState(value);

  if (step == null) {
    step = 0.1;
  }

  useEffect(() => {
    setControlValue(value);
  }, [value]);

  const onHandleDragged = (value: number) => {
    setControlValue(Number(value));
  };

  return (
    <BaseControl key={id} id={id} disabled={disabled}>
      <FeatureLabel disabled={disabled}>{feature + ":"}</FeatureLabel>
      <ImagerFeatureControlSlider
        disabled={disabled}
        value={value}
        min={min}
        max={max}
        onValueChanged={(value: number) => onValueChanged(id, value)}
        onHandleDraged={onHandleDragged}
        step={step}
        style={{
          flexGrow: 99,
          marginLeft: 5,
        }}
      />
      <ValueTextBox
        min={min}
        max={max}
        disabled={disabled}
        value={controlValue}
        onValueChanged={onValueChanged}
        id={id}
      />
      <UnitLabel disabled={disabled}>{unit}</UnitLabel>
    </BaseControl>
  );
}

type BaseControlProps = {
  id: string;
  children: React.ReactNode;
  disabled: boolean;
};

function BaseControl({ id, children, disabled }: BaseControlProps) {
  return (
    <div
      className={"base-control"}
      key={id}
      style={{
        color: disabled ? "#808080" : "#4F4F4F",
      }}
    >
      {children}
    </div>
  );
}

type DisplayValue = {
  display: string;
  value: string;
};

type BasicSelectionControlProps = {
  id: string;
  feature: string;
  onValueChanged: (id: string, value: string) => void;
  displayValues: DisplayValue[];
  disabled: boolean;
  value: string;
};

function BasicSelectionControl({
  id,
  feature,
  onValueChanged,
  displayValues,
  value,
  disabled,
}: BasicSelectionControlProps) {
  const dropDownDisplayValue = displayValues.map(
    (displayValue: DisplayValue) => ({
      display: displayValue.display,
      value: displayValue.value,
      disabled: false,
    })
  );
  return (
    <BaseControl key={id} id={id} disabled={disabled}>
      <FeatureLabel disabled={disabled}>{feature + ":"}</FeatureLabel>
      <div
        style={{
          flexGrow: 1,
          marginLeft: 15,
          marginRight: 15,
        }}
      >
        <CommonDropDown
          id={`drop-down-${id}`}
          value={value}
          onChange={(value) => onValueChanged(id, value)}
          style={{
            width: "100%",
            height: "30px",
            fontSize: 13.3,
          }}
          disabled={disabled}
          dropDownDisplayValues={dropDownDisplayValue}
        />
      </div>
    </BaseControl>
  );
}

type FeatureLabelProps = { children: React.ReactNode; disabled: boolean };

function FeatureLabel({ children, disabled }: FeatureLabelProps) {
  return (
    <div
      className="feature-label"
      style={{
        color: disabled ? "#808080" : "#4F4F4F",
        userSelect: "none",
      }}
    >
      {children}
    </div>
  );
}

type ValueTextBoxProps = {
  value: number;
  id: string;
  onValueChanged: (id: string, value: number) => void;
  disabled: boolean;
  min: number;
  max: number;
};

function ValueTextBox({
  value,
  id,
  onValueChanged,
  min,
  max,
  disabled,
}: ValueTextBoxProps) {
  const numberRegex = /^\d*\.?\d*$/;
  const [textState, setTextState] = useState("");

  useEffect(() => {
    setTextState(value.toFixed(2));
  }, [value]);

  const onChanged = (value: string) => {
    if (numberRegex.test(value)) {
      setTextState(value);
    }
  };

  const onValueBlur = () => {
    if (numberRegex.test(textState)) {
      let numberVal = Number.parseFloat(textState);
      if (numberVal < min) {
        numberVal = min;
      } else if (numberVal > max) {
        numberVal = max;
      }
      setTextState(numberVal.toString());
      onValueChanged(id, numberVal);
    }
  };

  const onKeyPressed = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onValueBlur();
    }
  };

  return (
    <div
      style={{
        border: "1px #8C8C8C",
      }}
    >
      <input
        onChange={(e) => onChanged(e.target.value)}
        onBlur={onValueBlur}
        onKeyDown={(e) => onKeyPressed(e)}
        style={{
          width: 52,
          height: 28,
          textAlign: "center",
          boxSizing: "border-box",
        }}
        disabled={disabled}
        value={textState}
        type="text"
      />
    </div>
  );
}

type UnitLabelProps = {
  children: React.ReactNode;
  disabled: boolean;
  style?: React.CSSProperties;
};

function UnitLabel({ children, disabled, style }: UnitLabelProps) {
  return (
    <div
      style={{
        width: 32,
        height: 28,
        boxSizing: "border-box",
        color: disabled ? "#808080" : "#000000",
        userSelect: "none",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

type UnitRadioProps = {
  id: string;
  label: string;
  checked: boolean;
  onValueChanged: (id: string, checked: boolean) => void;
  disabled: boolean;
};

function UnitRadio({
  id,
  label,
  checked,
  onValueChanged,
  disabled,
}: UnitRadioProps) {
  return (
    <div
      id={id}
      key={id}
      style={{
        marginLeft: "10px",
      }}
    >
      <label className="radio-button">
        {label}
        <input
          type="radio"
          onChange={(e) => onValueChanged(id, e.target.checked)}
          checked={checked}
          disabled={disabled}
        />
        <span className="radio-button-checkmark" />
      </label>
    </div>
  );
}

export {
  BasicControl,
  BasicSelectionControl,
  BaseControl,
  FeatureLabel,
  ToggleControl,
  ValueTextBox,
  UnitLabel,
  UnitRadio,
};
